import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { SelectField } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'
import { toast as message } from 'sonner'
import {
  StatusBadge,
  Notice,
  DateRangeField,
  SearchField,
} from '../products/PageHelpers'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import dayjs from 'dayjs'
import { getSubscriptions } from '../../services/subscriptions'
import { formatTimestamp } from '../../utils/format'
import { formatAmount } from '../../utils/orderDisplay'
import type {
  MerchantSubscriptionListItem,
  Pagination,
  SubscriptionStatus,
} from '../../types'
import {
  billingReasonText,
  canCancelSubscription,
  formatCard,
  formatSubscriptionTerm,
  invoiceStatusMeta,
  subscriptionStatusMeta,
} from './subscriptionLabels'

const initialPagination: Pagination = {
  page: 1,
  size: 20,
  total: 0,
  totalPages: 0,
}

const statusOptions: Array<{ value: SubscriptionStatus; label: string }> = [
  { value: 'PENDING', label: '待開通' },
  { value: 'ACTIVE', label: '生效中' },
  { value: 'PAST_DUE', label: '逾期' },
  { value: 'SUSPENDED', label: '已暫停' },
  { value: 'CANCEL_AT_PERIOD_END', label: '週期末取消' },
  { value: 'CANCELLED', label: '已取消' },
  { value: 'TERMINATED', label: '已終止' },
]

export default function SubscriptionList() {
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState<
    MerchantSubscriptionListItem[]
  >([])
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [status, setStatus] = useState<SubscriptionStatus | undefined>()
  const [subscriptionIdInput, setSubscriptionIdInput] = useState('')
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>()
  const [emailInput, setEmailInput] = useState('')
  const [customerEmail, setCustomerEmail] = useState<string | undefined>()
  const [planIdInput, setPlanIdInput] = useState('')
  const [planId, setPlanId] = useState<string | undefined>()
  const [createdRange, setCreatedRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null])
  const [nextBillingRange, setNextBillingRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null])

  const fetchSubscriptions = (
    page: number = 1,
    size: number = pagination.size || initialPagination.size,
  ) => {
    setLoading(true)
    const params: Parameters<typeof getSubscriptions>[0] = {
      page,
      size,
      status,
      subscriptionId,
      customerEmail,
      planId,
    }
    if (createdRange[0] && createdRange[1]) {
      params.createdStartAt = createdRange[0].startOf('day').valueOf()
      params.createdEndAt = createdRange[1].endOf('day').valueOf()
    }
    if (nextBillingRange[0] && nextBillingRange[1]) {
      params.nextBillingStartAt = nextBillingRange[0].startOf('day').valueOf()
      params.nextBillingEndAt = nextBillingRange[1].endOf('day').valueOf()
    }
    getSubscriptions(params)
      .then((res) => {
        setSubscriptions(res.data.items)
        setPagination(res.data.pagination)
        setLoadError(null)
      })
      .catch((error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : '載入訂閱列表失敗'
        message.error(errorMessage)
        setLoadError(errorMessage)
        setSubscriptions([])
        setPagination(initialPagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSubscriptions(1)
  }, [
    status,
    subscriptionId,
    customerEmail,
    planId,
    createdRange,
    nextBillingRange,
  ])

  const handleReset = () => {
    setStatus(undefined)
    setSubscriptionIdInput('')
    setSubscriptionId(undefined)
    setEmailInput('')
    setCustomerEmail(undefined)
    setPlanIdInput('')
    setPlanId(undefined)
    setCreatedRange([null, null])
    setNextBillingRange([null, null])
  }

  const columns = [
    {
      key: 'detail',
      header: '詳情',
      className: 'whitespace-nowrap',
      render: (row: MerchantSubscriptionListItem) => (
        <Button
          variant="link"
          onClick={() => navigate(`/dashboard/subscriptions/${row.id}`)}
        >
          訂閱詳情
        </Button>
      ),
    },
    {
      key: 'id',
      header: '訂閱 ID',
      render: (row: MerchantSubscriptionListItem) => row['id'] ?? '-',
    },
    {
      key: 'customer',
      header: '客戶',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return (
          <div className={'flex flex-col gap-3'}>
            <span>{record.customerEmail || '-'}</span>
            {record.customerName && (
              <span className="text-muted-foreground">
                {record.customerName}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'plan',
      header: '計劃',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return (
          <div className={'flex flex-col gap-3'}>
            <span>{record.planName || record.planId}</span>
            <span className="text-right text-xs text-muted-foreground tabular-nums">
              {formatAmount(record.planAmount)}
              {record.planCurrency}/{' '}
              {formatSubscriptionTerm(
                record.billingInterval,
                record.billingIntervalCount,
                record.totalBillingCycles,
              )}
            </span>
          </div>
        )
      },
    },
    {
      key: 'status',
      header: '狀態',
      render: (row: MerchantSubscriptionListItem) => {
        const value = row['status']
        const meta = subscriptionStatusMeta[value]
        return <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
      },
    },
    {
      key: 'paymentMethod',
      header: '支付方式',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return formatCard(record.cardBrand, record.cardLast4)
      },
    },
    {
      key: 'latestInvoice',
      header: '最近扣款',
      className: 'text-right tabular-nums',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        const invoice = record.latestInvoice
        if (!invoice) return '-'
        const meta = invoiceStatusMeta[invoice.status]
        return (
          <div className={'flex flex-col gap-3'}>
            <span>
              {formatAmount(invoice.amount)} {invoice.currency}{' '}
              <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
            </span>
            <span className="text-muted-foreground">
              {billingReasonText[invoice.billingReason]}·{' '}
              {formatTimestamp(invoice.paidAt || invoice.updatedAt)}
            </span>
          </div>
        )
      },
    },
    {
      key: 'period',
      header: '當前週期',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return (
          <span>
            {formatTimestamp(record.currentPeriodStartAt)} ～{' '}
            {formatTimestamp(record.currentPeriodEndAt)}
          </span>
        )
      },
    },
    {
      key: 'nextBillingAt',
      header: '下次扣款 / 取消',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return (
          <div className={'flex flex-col gap-3'}>
            <span>{formatTimestamp(record.nextBillingAt)}</span>
            {record.cancelAtPeriodEnd && (
              <StatusBadge color={'blue'}>
                週期末取消：{formatTimestamp(record.cancelAt)}
              </StatusBadge>
            )}
            {record.cancelledAt && (
              <span className="text-muted-foreground">
                已取消：{formatTimestamp(record.cancelledAt)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'createdAt',
      header: '創建時間',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantSubscriptionListItem) => {
        const value = row['createdAt']
        return formatTimestamp(value)
      },
    },
    {
      key: 'actions',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (row: MerchantSubscriptionListItem) => {
        const record = row
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Button
              onClick={(event) => {
                event.stopPropagation()
                navigate(`/dashboard/subscriptions/${record.id}`)
              }}
              variant={'link'}
              type="button"
              size={'sm'}
            >
              詳情
            </Button>
            {canCancelSubscription(record.status) && (
              <StatusBadge color={'orange'}>可取消</StatusBadge>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <h2
        style={{ marginBottom: 16 }}
        className="mb-4 text-xl font-semibold tracking-tight"
      >
        訂閱管理
      </h2>
      <Card>
        <div className="space-y-4 px-5">
          <div
            style={{ marginBottom: 16 }}
            className={'flex flex-wrap items-center gap-2'}
          >
            <SearchField
              placeholder={'訂閱 ID'}
              style={{ width: 260 }}
              value={subscriptionIdInput}
              onChange={(event) => setSubscriptionIdInput(event.target.value)}
              onSearch={(value) => setSubscriptionId(value.trim() || undefined)}
            />
            <SearchField
              placeholder={'客戶郵箱'}
              style={{ width: 240 }}
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              onSearch={(value) => setCustomerEmail(value.trim() || undefined)}
            />
            <SearchField
              placeholder={'計劃 ID'}
              style={{ width: 240 }}
              value={planIdInput}
              onChange={(event) => setPlanIdInput(event.target.value)}
              onSearch={(value) => setPlanId(value.trim() || undefined)}
            />
            <div style={{ width: 170 }}>
              <SelectField
                aria-label="訂閱狀態"
                options={[{ value: '', label: '全部' }, ...statusOptions]}
                placeholder={'訂閱狀態'}
                value={status}
                onValueChange={(value) =>
                  setStatus((value as SubscriptionStatus) || undefined)
                }
              />
            </div>
            <DateRangeField
              value={createdRange}
              onChange={(dates) => setCreatedRange(dates || [null, null])}
              placeholder={['創建開始', '創建結束']}
            />
            <DateRangeField
              value={nextBillingRange}
              onChange={(dates) => setNextBillingRange(dates || [null, null])}
              placeholder={['下次扣款開始', '下次扣款結束']}
            />
            <Button onClick={handleReset} variant={'outline'} type="button">
              重置
            </Button>
          </div>
          {loadError && (
            <Notice
              tone={'error'}
              title={'載入訂閱列表失敗'}
              description={loadError}
              action={
                <Button
                  onClick={() =>
                    fetchSubscriptions(
                      pagination.page || 1,
                      pagination.size || initialPagination.size,
                    )
                  }
                  variant={'outline'}
                  type="button"
                  size={'sm'}
                >
                  重新載入
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <DataTable
            rowKey={'id'}
            loading={loading}
            columns={columns}
            data={subscriptions}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onChange: (page, size) => fetchSubscriptions(page, size),
            }}
          />
        </div>
      </Card>
    </div>
  )
}
